import React, { lazy, useCallback, useEffect, useMemo } from 'react';
import { PartnerLogo } from './styles';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Menu } from '../../../components/UI-CHAKRA/Menu/Menu';
import { MenuButton } from '../../../components/UI-CHAKRA/Menu/MenuButton';
import { MenuList } from '../../../components/UI-CHAKRA/Menu/MenuList';
import { ReactSVG } from 'react-svg';
import threePoints from '../../../assets/images/icons/three_points.svg';
import { ReactComponent as Avatar } from '../../../assets/images/icons/avatar.svg';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { HEADER_HEIGHT, HEADER_HEIGHT_MOBILE } from '../../../contants/size';
import { get } from 'lodash';
import withSuspense from '../../../components/withLazyComponent';
import BannerComponent from '../../../components/BannerComponent';
import { RQE } from '../../../interfaces/users';

const GameficationHeader = withSuspense(lazy(() => import('./Gamefication/GameficationHeader')));

interface PrescriptionHeaderProps {
  showMenu?: boolean;
}

function PrescriptionHeader(props: PrescriptionHeaderProps) {
  const isMobile = useIsMobile();

  const logoUrl = usePrescriptionStore((store) => store.prescription?.LogoURL || null);
  const validationCode = usePrescriptionStore((store) => store.validationCode());
  const benefit = usePrescriptionStore((store) => store.benefit());
  const showGamefication = usePrescriptionStore((store) =>
    get(store, 'prescriberConfiguration.Features.enableGamefication', false)
  );

  const { prescription, prescriberConfiguration, setIsBannerShown } = usePrescriptionStore();
  const [shouldShowBanner, setShouldShowBanner] = React.useState<boolean>(false);

  useEffect(() => {
    const enableBanner = prescriberConfiguration?.Features?.enablePharmacyAds;

    const bannerPropsReady =
      prescriberConfiguration?.idParceiro !== undefined && prescription?.Prescritor?.Especialidades !== undefined;

    if (enableBanner && bannerPropsReady) {
      setShouldShowBanner(true);
      setIsBannerShown(true);
    } else {
      setShouldShowBanner(false);
      setIsBannerShown(false);
    }
  }, [prescriberConfiguration, prescription, setIsBannerShown]);

  const closeBanner = useCallback(() => {
    setShouldShowBanner(false);
    setIsBannerShown(false);
  }, [setIsBannerShown]);

  const PrescriptionCode = useMemo(() => {
    return (
      <Box
        bgColor={'white'}
        border={'1px solid'}
        borderColor={'secondary.main'}
        height={'60px'}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        flexDirection={'column'}
        borderRadius={'8px'}
        padding={'0 22px'}
      >
        <Text fontSize={'12px'} fontWeight={'400'} color={'#16002C'} style={{ lineHeight: '18px' }}>
          token da receita
        </Text>
        <Text
          fontSize={'24px'}
          fontFamily={'Roboto Mono'}
          fontWeight={'700'}
          color={'#16002C'}
          style={{ lineHeight: '24px' }}
        >
          {validationCode}
        </Text>
      </Box>
    );
  }, [validationCode]);

  const Logo = useMemo(() => {
    if (logoUrl === null) return null;

    if (isMobile) {
      return (
        <>
          <Box mb={'24px'} display={'flex'} justifyContent={'center'}>
            <PartnerLogo src={logoUrl} />
          </Box>
          <Box h={'1px'} bg={'gray.darkest'} mb={'24px'} />
        </>
      );
    }

    return (
      <Box display={'flex'} alignItems={'center'} h={'100%'}>
        <PartnerLogo src={logoUrl} />
      </Box>
    );
  }, [logoUrl, isMobile]);

  const MoreInfoPrescription = useMemo(() => {
    if (isMobile)
      return (
        <Menu isLazy>
          <MenuButton
            width={'40px'}
            height={'40px'}
            bg={'transparent'}
            borderRadius={'10px'}
            border={'1px solid'}
            borderColor={'primary.main'}
            fill={'primary.main'}
          >
            <ReactSVG src={threePoints} style={{ display: 'flex', justifyContent: 'center' }} />
          </MenuButton>
          <MenuList borderRadius={'20px'} w={'250px'} maxH={'210px'} zIndex={20} p={'25px'}>
            {Logo}
            {PrescriptionCode}
          </MenuList>
        </Menu>
      );

    return PrescriptionCode;
  }, [isMobile, PrescriptionCode, Logo]);

  return (
    <Box>
      {isMobile && shouldShowBanner === true && (
        <BannerComponent
          closeBanner={closeBanner}
          partnerId={prescriberConfiguration?.idParceiro as number}
          partnerName={prescriberConfiguration?.Nome}
          establishmentName={prescription?.Estabelecimento?.Nome}
          patientAge={prescription?.Paciente?.Idade as string}
          doctorSpecialities={prescription?.Prescritor?.RQEs as RQE[]}
          prescriptionId={prescription?.idPrescricao}
          prescriberId={prescription?.idPrescritor}
          validationCode={prescription?.CodigoValidacao}
        />
      )}
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        padding={'0 5px 0 12px'}
        borderBottom={'1px solid #e9ecee'}
        height={{ base: HEADER_HEIGHT_MOBILE, sm: HEADER_HEIGHT }}
      >
        <Box display={'flex'} alignItems={'center'}>
          <Box position={'relative'} height={'56px'}>
            <Box display={'flex'} height={'100%'} alignItems={'center'} fill={'primary.main'}>
              <Avatar />
            </Box>

            {benefit && (
              <div
                style={{
                  width: 20,
                  height: 20,
                  position: 'absolute',
                  bottom: 3,
                  right: -6,
                  borderRadius: 50,
                  border: '2px solid white',
                  backgroundColor: benefit.CorFundoEtiqueta,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <img
                  src={benefit.IconeCabecalho}
                  style={{ maxWidth: '100%', borderRadius: 'inherit' }}
                  alt={`${benefit.NomeBeneficio} logo`}
                />
              </div>
            )}
          </Box>

          <Box margin={'0 16px'}>
            <Text fontSize={'12px'} color={'#868E96'} lineHeight={'14px'}>
              Paciente
              {benefit && (
                <Text as={'span'} color={'#007CFF'} fontSize={'12px'} fontWeight={500}>
                  {` ${benefit.NomeBeneficio}`}
                </Text>
              )}
            </Text>
            <Text fontSize={'14px'} lineHeight={'14px'}>
              {prescription?.Paciente.NomeSocial ? (
                <>
                  <b>{prescription?.Paciente.NomeSocial}</b> ({prescription?.Paciente.Nome})
                </>
              ) : (
                prescription?.Paciente.Nome
              )}
            </Text>
          </Box>
          {!isMobile && Logo}
        </Box>
        {!isMobile && shouldShowBanner === true && (
          <BannerComponent
            closeBanner={closeBanner}
            partnerId={prescriberConfiguration?.idParceiro as number}
            partnerName={prescriberConfiguration?.Nome}
            establishmentName={prescription?.Estabelecimento?.Nome}
            patientAge={prescription?.Paciente?.Idade as string}
            doctorSpecialities={prescription?.Prescritor?.RQEs as RQE[]}
            prescriptionId={prescription?.idPrescricao}
            prescriberId={prescription?.idPrescritor}
            validationCode={prescription?.CodigoValidacao}
          />
        )}
        <Flex gap="30px">
          {showGamefication && <GameficationHeader />}
          {MoreInfoPrescription}
        </Flex>
      </Box>
    </Box>
  );
}

export default React.memo(PrescriptionHeader);
